import validator from "validator";
import {Validator, validateForm} from "../components/form/validation.ts";
import {ALL_LEGAL_ENTITY_TYPES, getLabelForLegalEntityType} from "../types/enums.ts";
import type {LegalEntityType} from "../generated/enums.ts";

export const LEGAL_ENTITY_TYPE_OPTIONS = ALL_LEGAL_ENTITY_TYPES.map((value) => ({
    value,
    label: getLabelForLegalEntityType(value),
}));

export const DEFAULT_LEGAL_ENTITY_TYPE: LegalEntityType = 'limited-liability';
export const DEFAULT_FTE_AMOUNT = 1;

export interface BusinessRegistrationFormData {
    name: string;
    legal_entity_type: LegalEntityType;
    registration_number: string;
    fte_amount: number;
    industry: string;
    description: string;
}

export const EMPTY_BUSINESS_REGISTRATION_FORM: BusinessRegistrationFormData = {
    name: "",
    legal_entity_type: DEFAULT_LEGAL_ENTITY_TYPE,
    registration_number: "",
    fte_amount: DEFAULT_FTE_AMOUNT,
    industry: "",
    description: "",
};

export const businessRegistrationValidators: Array<Validator<BusinessRegistrationFormData>> = [
    {
        fieldName: "name",
        validationFunction: (data) => validator.isLength(data.name, {min: 1, max: 255}),
        message: "Bedrijfsnaam moet tussen 1 en 255 tekens zijn",
    },
    {
        fieldName: "registration_number",
        validationFunction: (data) => validator.isLength(data.registration_number, {min: 1, max: 255}),
        message: "Registratienummer moet tussen 1 en 255 tekens zijn",
    },
    {
        fieldName: "legal_entity_type",
        validationFunction: (data) => ALL_LEGAL_ENTITY_TYPES.includes(data.legal_entity_type),
        message: "Selecteer een geldige rechtsvorm",
    },
    {
        fieldName: "fte_amount",
        validationFunction: (data) => validator.isInt(`${data.fte_amount}`, {min: 1, max: 255}),
        message: "FTE aantal moet een getal zijn tussen 1 en 255",
    },
    {
        fieldName: "industry",
        validationFunction: (data) => validator.isLength(data.industry, {min: 1, max: 255}),
        message: "Industrie moet tussen 1 en 255 tekens zijn",
    },
    {
        fieldName: "description",
        validationFunction: (data) => validator.isLength(data.description, {min: 1, max: 2048}),
        message: "Bedrijfsbeschrijving moet tussen 1 en 2048 tekens zijn",
    },
];

export function validateBusinessRegistrationForm(
    data: BusinessRegistrationFormData,
): Record<string, string> {
    const result = validateForm(data, businessRegistrationValidators);
    const errors: Record<string, string> = {};

    for (const todo of result.todos) {
        if (!errors[todo.fieldName]) {
            errors[todo.fieldName] = todo.message;
        }
    }

    return errors;
}

export function toCreateBusinessApiPayload(data: BusinessRegistrationFormData) {
    return {
        name: data.name.trim(),
        legal_entity_type: data.legal_entity_type,
        registration_number: data.registration_number.trim(),
        fte_amount: data.fte_amount,
        industry: data.industry.trim(),
        description: data.description.trim(),
    };
}

export type CreateBusinessApiPayload = ReturnType<typeof toCreateBusinessApiPayload>;
